'use client';

import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  clearListingUrlAlertSuppression,
  evaluateAdminAlerts,
  getAdminAlertConfig,
  getAdminAlertHistory,
  getAdminAlertStatus,
  getAdminListingUrlAlerts,
  loadAdminSession,
  markListingUrlAlertFalsePositive,
  updateAdminAlertConfig,
  type ListingUrlAlertStatus,
  type PatchAdminAlertConfigInput,
} from '@nomiss/services';

function hasAdminAccessToken(): boolean {
  return Boolean(loadAdminSession()?.accessToken);
}

export const adminAlertsQueryKeys = {
  all: ['admin', 'alerts'] as const,
  config: () => [...adminAlertsQueryKeys.all, 'config'] as const,
  status: () => [...adminAlertsQueryKeys.all, 'status'] as const,
  history: () => [...adminAlertsQueryKeys.all, 'history'] as const,
  listingUrls: (status: ListingUrlAlertStatus) =>
    [...adminAlertsQueryKeys.all, 'listing-urls', status] as const,
};

export function useAdminAlertConfig(options?: { enabled?: boolean }) {
  const adminAlertConfigQuery = useQuery({
    queryKey: adminAlertsQueryKeys.config(),
    queryFn: () => getAdminAlertConfig(),
    enabled: (options?.enabled ?? true) && hasAdminAccessToken(),
  });

  return { adminAlertConfigQuery };
}

export function useAdminAlertStatus(options?: { enabled?: boolean }) {
  const adminAlertStatusQuery = useQuery({
    queryKey: adminAlertsQueryKeys.status(),
    queryFn: () => getAdminAlertStatus(),
    enabled: (options?.enabled ?? true) && hasAdminAccessToken(),
  });

  return { adminAlertStatusQuery };
}

export function useAdminAlertHistory(options?: {
  enabled?: boolean;
  page?: number;
  limit?: number;
}) {
  const page = options?.page ?? 1;
  const limit = options?.limit ?? 20;
  const adminAlertHistoryQuery = useQuery({
    queryKey: [...adminAlertsQueryKeys.history(), page, limit],
    queryFn: () => getAdminAlertHistory({ page, limit }),
    enabled: (options?.enabled ?? true) && hasAdminAccessToken(),
    placeholderData: keepPreviousData,
  });

  return { adminAlertHistoryQuery };
}

export function useUpdateAdminAlertConfig() {
  const queryClient = useQueryClient();

  const updateAdminAlertConfigMutation = useMutation({
    mutationFn: (input: PatchAdminAlertConfigInput) => updateAdminAlertConfig(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminAlertsQueryKeys.all });
    },
  });

  return { updateAdminAlertConfigMutation };
}

export function useEvaluateAdminAlerts() {
  const queryClient = useQueryClient();

  const evaluateAdminAlertsMutation = useMutation({
    mutationFn: (input?: { dryRun?: boolean }) => evaluateAdminAlerts(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminAlertsQueryKeys.status() });
      void queryClient.invalidateQueries({ queryKey: adminAlertsQueryKeys.history() });
    },
  });

  return { evaluateAdminAlertsMutation };
}

export function useAdminListingUrlAlerts(
  status: ListingUrlAlertStatus,
  options?: { enabled?: boolean },
) {
  const adminListingUrlAlertsQuery = useQuery({
    queryKey: adminAlertsQueryKeys.listingUrls(status),
    queryFn: () => getAdminListingUrlAlerts({ status }),
    enabled: (options?.enabled ?? true) && hasAdminAccessToken(),
  });

  return { adminListingUrlAlertsQuery };
}

export function useMarkListingUrlFalsePositive() {
  const queryClient = useQueryClient();
  const markListingUrlFalsePositiveMutation = useMutation({
    mutationFn: (id: string) => markListingUrlAlertFalsePositive(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminAlertsQueryKeys.all });
    },
  });
  return { markListingUrlFalsePositiveMutation };
}

export function useClearListingUrlSuppression() {
  const queryClient = useQueryClient();
  const clearListingUrlSuppressionMutation = useMutation({
    mutationFn: (id: string) => clearListingUrlAlertSuppression(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminAlertsQueryKeys.all });
    },
  });
  return { clearListingUrlSuppressionMutation };
}
